import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  CssBaseline, 
  Divider, 
  Drawer, 
  IconButton, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Typography, 
  Button,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Snackbar,
  Alert,
  Slide,
  Popover,
  Paper,
  Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';

import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import CampaignIcon from '@mui/icons-material/Campaign';
import ArchiveIcon from '@mui/icons-material/Archive';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BarChartIcon from '@mui/icons-material/BarChart';

import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';

// Sidebar collapses on small screens, scales slightly on very large displays
const drawerWidth = 240;
const drawerWidthLg = 260; // slightly wider on 1440p+

// All navigation items with role-based access control
const allNavItems = [
  { 
    text: 'Dashboard', 
    path: '/app', 
    icon: <DashboardIcon />, 
    roles: ['admin', 'manager', 'sales', 'designer'] // All roles can see dashboard
  },
  { 
    text: 'Organisations', 
    path: '/app/organizations', 
    icon: <BusinessIcon />, 
    roles: ['admin', 'manager', 'sales', 'designer'] // All roles can see organizations
  },
  { 
    text: 'Orders', 
    path: '/app/orders', 
    icon: <MonetizationOnIcon />, 
    roles: ['admin'] // Admins only
  },
  { 
    text: 'Ad Orders', 
    path: '/app/workflow', 
    icon: <AssignmentIcon />, 
    roles: ['admin', 'manager', 'designer'] // Admins, Managers, and Designers only
  },
  { 
    text: 'Stationery Orders', 
    path: '/app/stationery-orders', 
    icon: <AssignmentIcon />, 
    roles: ['admin', 'manager', 'designer'] // Admins, Managers, and Designers only
  },
  { 
    text: 'Sales Workflow', 
    path: '/app/sales-workflow', 
    icon: <TrendingUpIcon />, 
    roles: ['admin', 'manager', 'sales'] // Admins, Managers, and Sales only
  },
  { 
    text: 'Business Outreach', 
    path: '/app/outreach', 
    icon: <CampaignIcon />, 
    roles: ['admin', 'manager', 'sales'] // Admins, Managers, and Sales only
  },
  { 
    text: 'School Workflow', 
    path: '/app/school-workflow', 
    icon: <SchoolIcon />, 
    roles: ['admin'] // Admins only
  },
  { 
    text: 'Enquiries', 
    path: '/app/enquiries', 
    icon: <ContactPhoneIcon />, 
    roles: ['admin'] // Admins only
  },
  { 
    text: 'Archive', 
    path: '/app/archive', 
    icon: <ArchiveIcon />, 
    roles: ['admin'] // Admins only
  },
  { 
    text: 'Users', 
    path: '/app/users', 
    icon: <PeopleIcon />, 
    roles: ['admin'] // Admins only
  },
  { 
    text: 'Finance', 
    path: '/app/finance', 
    icon: <AccountBalanceIcon />, 
    roles: ['admin', 'manager'] // Admins and Managers only
  },
  { 
    text: 'Stats', 
    path: '/app/stats', 
    icon: <BarChartIcon />, 
    roles: ['admin'] // Admins only
  },
  { 
    text: 'Settings', 
    path: '/app/settings', 
    icon: <SettingsIcon />, 
    roles: ['admin', 'manager'] // Admins and Managers only
  },
];



function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [impersonationNotice, setImpersonationNotice] = useState('');
  const { user, logout, role, isImpersonating, stopImpersonation } = useAuth();
  const { unreadCount, notifications, toastQueue, dismissToast, markAsRead } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  const unreadNotifications = notifications.filter(n => !n.read).slice(0, 5);

  const handleNotifClick = (event) => {
    if (unreadCount > 0) {
      setNotifAnchorEl(event.currentTarget);
    } else {
      navigate('/app/notifications');
    }
  };

  const handleNotifClose = () => setNotifAnchorEl(null);

  const handleNotifItemClick = (notification) => {
    markAsRead(notification.id);
    handleNotifClose();
  };

  const handleViewAll = () => {
    handleNotifClose();
    navigate('/app/notifications');
  };

  useEffect(() => {
    const onBlocked = (event) => {
      setImpersonationNotice(event.detail?.message || "You're viewing as another user — actions are disabled.");
    };
    window.addEventListener('impersonation-blocked', onBlocked);
    return () => window.removeEventListener('impersonation-blocked', onBlocked);
  }, []);

  const handleStopImpersonation = () => {
    stopImpersonation();
    navigate('/app/users');
  };

  // Filter navigation items based on user role
  const getFilteredNavItems = () => {
    if (!role) return []; // If no role, show nothing
    
    // Normalize role from Auth0 (handle both "Administrator" and "admin")
    const normalizedRole = role.toLowerCase() === 'administrator' ? 'admin' : role.toLowerCase();
    
    return allNavItems.filter(item => {
      return item.roles.includes(normalizedRole);
    });
  };

  const filteredNavItems = getFilteredNavItems();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose(); // Close the menu first
    
    try {
      // Clear any client-side data before logging out
      localStorage.removeItem('redirectInProgress');
      
      // Use Auth context's logout which will handle the Auth0 logout
      await logout();
      
      // In case the Auth0 redirect doesn't happen, force navigation
      // This should only run if there's an issue with Auth0 logout
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    } catch (error) {
      console.error('Error during logout:', error);
      alert('Logout failed. Please try again.');
    }
  };

  // No need for separate admin filtering anymore - it's all in filteredNavItems

  // Menu options
  const profileMenu = [
    { text: 'Profile', action: () => navigate('/app/profile') },
    { text: 'Logout', action: handleLogout }
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
          AustnewsCRM
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {filteredNavItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              component={Link} 
              to={item.path}
              selected={location.pathname === item.path}

            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>


    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: {
            sm: `calc(100% - ${drawerWidth}px)`,
            xl: `calc(100% - ${drawerWidthLg}px)`,
          },
          ml: {
            sm: `${drawerWidth}px`,
            xl: `${drawerWidthLg}px`,
          },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {filteredNavItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2">
                {user?.name || 'User'}
              </Typography>
            </Box>
            
            {/* Notifications Icon */}
            <IconButton
              size="large"
              color="inherit"
              aria-label="notifications"
              onClick={handleNotifClick}
              sx={{ mr: 1 }}
            >
              <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>

            {/* Unread notifications dropdown */}
            <Popover
              open={Boolean(notifAnchorEl)}
              anchorEl={notifAnchorEl}
              onClose={handleNotifClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { width: 360, maxHeight: 480, mt: 0.5 } }}
            >
              <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Unread Notifications
                </Typography>
                <Chip label={unreadCount} color="error" size="small" />
              </Box>
              <List disablePadding>
                {unreadNotifications.map((n) => (
                  <ListItem
                    key={n.id}
                    disablePadding
                    divider
                  >
                    <ListItemButton onClick={() => handleNotifItemClick(n)} sx={{ py: 1.5, px: 2 }}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {n.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {n.message}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                          {new Date(n.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
                {unreadCount > 5 && (
                  <ListItem disablePadding>
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        +{unreadCount - 5} more unread
                      </Typography>
                    </Box>
                  </ListItem>
                )}
              </List>
              <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button fullWidth variant="outlined" size="small" onClick={handleViewAll}>
                  View All Notifications
                </Button>
              </Box>
            </Popover>
            
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="account"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              {profileMenu.map((item) => (
                <MenuItem key={item.text} onClick={item.action}>
                  {item.text}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth, xl: drawerWidthLg },
          flexShrink: { sm: 0 },
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: { sm: drawerWidth, xl: drawerWidthLg },
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Responsive padding: tighter on mobile, generous on large screens
          p: { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 3.5 },
          width: {
            sm: `calc(100% - ${drawerWidth}px)`,
            xl: `calc(100% - ${drawerWidthLg}px)`,
          },
          minWidth: 0, // prevent flex children from overflowing
          overflowX: 'hidden',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        {isImpersonating && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleStopImpersonation}>
                Exit
              </Button>
            }
          >
            Viewing as {user?.name} — read only
          </Alert>
        )}
        <Outlet />
      </Box>

      {/* Notification toasts ? renders one at a time, oldest first */}
      {toastQueue.length > 0 && (() => {
        const toast = toastQueue[0];
        const severityMap = { success: 'success', warning: 'warning', error: 'error', info: 'info' };
        const severity = severityMap[toast.type] || 'info';
        return (
          <Snackbar
            key={toast.toastId}
            open
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            TransitionComponent={Slide}
            TransitionProps={{ direction: 'down' }}
            autoHideDuration={6000}
            onClose={(event, reason) => {
              // Auto-timeout: just remove the toast, leave it unread
              if (reason === 'timeout') { dismissToast(toast.toastId, { markRead: false }); return; }
            }}
            sx={{ mt: '64px' }}
          >
            <Alert
              onClose={() => dismissToast(toast.toastId, { markRead: true })}
              severity={severity}
              variant="filled"
              sx={{ minWidth: 300, maxWidth: 420, boxShadow: 4 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.25 }}>
                {toast.title}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.92 }}>
                {toast.message}
              </Typography>
              {toastQueue.length > 1 && (
                <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mt: 0.5 }}>
                  +{toastQueue.length - 1} more notification{toastQueue.length - 1 !== 1 ? 's' : ''}
                </Typography>
              )}
            </Alert>
          </Snackbar>
        );
      })()}

      <Snackbar
        open={Boolean(impersonationNotice)}
        autoHideDuration={4000}
        onClose={() => setImpersonationNotice('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setImpersonationNotice('')}>
          {impersonationNotice}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DashboardLayout; 
